/**
 * Middleware 用 公開ルート集約レジストリ
 *
 * 各 feature の `route-config.ts` を static import して、middleware が
 * 認証バイパスすべき path 一覧を組み立てる。
 *
 * 設計方針:
 * - middleware は Edge Runtime で動くため動的 import 不可
 * - 全 feature を static import して中央で集約する形
 * - 各 feature 側は **純定数 export のみ** （関数呼び出し・副作用なし）
 * - 共通 path（/login, /api/auth 等）は本ファイル内で直接定義
 *
 * @see docs/計画/2026-05-09-ICpochipochi-漏れ抽象化.md L-A3
 */

// apps/ 配下の feature
// eslint-disable-next-line gforce/no-lib-to-app-projects-import -- 後続対応 (Issue #1893 で本格 cleanup 予定)
import { CUSTOMER_PORTAL_PUBLIC_PATHS } from '@/app/projects/apps/customer-portal/route-config'
// eslint-disable-next-line gforce/no-lib-to-app-projects-import -- 後続対応 (Issue #1893 で本格 cleanup 予定)
import { ICPOCHIPOCHI_PUBLIC_PATHS } from '@/app/projects/apps/ICpochipochi/route-config'
// eslint-disable-next-line gforce/no-lib-to-app-projects-import -- 後続対応 (Issue #1893 で本格 cleanup 予定)
import { G_PLAN_PUBLIC_PATHS } from '@/app/projects/apps/g-plan/route-config'
// eslint-disable-next-line gforce/no-lib-to-app-projects-import -- 後続対応 (Issue #1893 で本格 cleanup 予定)
import { OPEN_PUBLIC_PATHS } from '@/app/projects/open/route-config'

// app/ 直下の feature
import { INTAKE_PUBLIC_PATHS } from '@/app/intake-v4/route-config'
import { HEARING_PUBLIC_PATHS } from '@/app/hearing/route-config'
import { BUILDING_APPLICATION_PUBLIC_PATHS } from '@/app/building-application/route-config'
import { CUSTOMER_MYPAGE_PUBLIC_PATHS } from '@/app/customer-mypage/route-config'
import { EVENTS_PUBLIC_PATHS } from '@/app/events/route-config'

// API のみの feature
import { LINEWORKS_PUBLIC_PATHS } from '@/app/api/lineworks/route-config'
import { INVITATIONS_PUBLIC_PATHS } from '@/app/api/invitations/route-config'
import { REFERRALS_PUBLIC_PATHS } from '@/app/api/referrals/route-config'
import { LIFE_X_PUBLIC_PATHS } from '@/app/api/life-x/route-config'
import { EXTERNAL_LIFEX_PUBLIC_PATHS } from '@/app/api/external/lifex/route-config'

/**
 * feature に紐付かない、認証フロー・運用基盤の公開ページパス。
 */
const COMMON_PUBLIC_PAGE_PATHS = [
  '/login',
  '/reset-password',
  '/invite',
  '/',
  // Sentry トンネルルート（next.config.ts tunnelRoute: '/monitoring'）
  // セッションクッキーなしで POST されるため認証チェックをバイパスする
  '/monitoring',
  // 設定の「自分の LINE を登録」で出たリンクを自分の LINE で開く 1 枚（認可は /api/line-link/self が ID トークンと 30 分・1 回限りの印で行う）
  '/line/self',
  // LIFF の endpoint の入口（liff.init のあと liff.state の行き先へ移るだけ）。判定は「完全一致か配下」なので /line は配下の /line/self も含む
  '/line',
  // 資料請求の LIFF のフォームと完了（認可は /api/line/document-request が ID トークンで行う）
  '/line/request-docs',
] as const

/**
 * feature に紐付かない、認証フロー・運用基盤の公開 API パス。
 */
const COMMON_PUBLIC_API_PATHS = [
  '/api/auth',
  '/api/health',
  '/api/webhooks',
  '/api/cron',
  '/api/setup/claude-hook',
  '/api/open',
  // 日報HTMLプロキシ: Google Chat から認証なしでアクセスされる公開エンドポイント
  '/api/reports/daily',
  // 🔴 MCP（Claude Code から繋ぐ口）。**素通しにする意味ではない。**
  //    セッション Cookie ではなく **Bearer トークン**で来るため、middleware の
  //    「セッションが無ければ /api/* は 401」に必ず引っかかる。ここへ登録しないと
  //    私たちのルートに1度も到達しない（2026-09-11 に dev で実測。実装が返すはずの
  //    jsonrpc 形式ではなく {"error":"Unauthorized"} が返っていた）。
  //    鍵の検証・Origin 検証・レート制限は app/api/mcp/improvement-requests/route.ts が行う。
  //    /api/webhooks や /api/cron と同じ扱い（どちらも自前で署名や Bearer を検証している）。
  '/api/mcp',
  // CardDAV 社員電話帳（PROPFIND/REPORT は middleware-impl.ts の relayCarddavRequest が
  // 署名付き内部POSTへ変換してからここへ来る。GET/HEAD/OPTIONS/POSTは素通し）。
  // 認証はセッションCookieではなく Basic Auth（route側の verifyCredential）で自前検証するため、
  // /api/mcp と同じ理由でここへ登録しないとセッション無し401に必ず引っかかる。
  '/api/carddav',
  // 設定の「自分の LINE を登録」の LIFF から呼ぶ API。セッションではなく LINE の ID トークンと 1 回限りの印で自前検証する
  '/api/line-link/self',
  // LIFF の資料請求（10/20）。セッションではなく LINE の ID トークンで自前検証し、IP の rate-limit をかける
  '/api/line/document-request',
  // LIFF の資料請求フォームの郵便番号→住所（7 桁だけ受け、rate-limit をかける。/api/postal は公開にしない）
  '/api/line/postal',
  // LIFF の来場予約の受け口（空き枠・予約・一覧・変更・取消）。セッションではなく LINE の ID トークンで自前検証し、IP と本人（sub）の rate-limit をかける。受付が OFF の間は 403
  '/api/line/reserve',
] as const

/** CardDAV 自動探索先（iOS 等が最初に叩く固定パス・v1本文）。画面ではなく middleware が 301 を返す入口なので公開 API 側に置く */
export const CARDDAV_PUBLIC_WELL_KNOWN_PATH = '/.well-known/carddav'

/** middleware が認証不要と判定する全公開ページパス */
export const ALL_PUBLIC_PAGE_PATHS: readonly string[] = [
  ...COMMON_PUBLIC_PAGE_PATHS,
  ...CUSTOMER_PORTAL_PUBLIC_PATHS.pages,
  ...ICPOCHIPOCHI_PUBLIC_PATHS.pages,
  ...G_PLAN_PUBLIC_PATHS.pages,
  ...OPEN_PUBLIC_PATHS.pages,
  ...INTAKE_PUBLIC_PATHS.pages,
  ...HEARING_PUBLIC_PATHS.pages,
  ...BUILDING_APPLICATION_PUBLIC_PATHS.pages,
  ...CUSTOMER_MYPAGE_PUBLIC_PATHS.pages,
  ...EVENTS_PUBLIC_PATHS.pages,
  ...LINEWORKS_PUBLIC_PATHS.pages,
  ...INVITATIONS_PUBLIC_PATHS.pages,
  ...REFERRALS_PUBLIC_PATHS.pages,
  ...LIFE_X_PUBLIC_PATHS.pages,
]

/**
 * 申請フォームのホーム画面のアイコンの設定（画面ではなくブラウザが読むファイルなので API 側に置く）。
 * ブラウザはログインの情報を付けずに読むので、公開しないとログイン画面へ送られ、アイコンが全体の「/」へ飛ぶ
 * （2026-11-05・app/projects/kouji-task/shared/lib/extra-cost-app/extra-cost-form-app.ts と同じ文字列）。
 * 公開するのはこの1本だけで、申請フォーム本体は開かない。
 */
const EXTRA_COST_FORM_MANIFEST_PUBLIC_PATH = '/kouji-task/extra-cost/manifest.webmanifest'

/** middleware が認証不要と判定する全公開 API パス */
export const ALL_PUBLIC_API_PATHS: readonly string[] = [
  ...COMMON_PUBLIC_API_PATHS,
  CARDDAV_PUBLIC_WELL_KNOWN_PATH,
  EXTRA_COST_FORM_MANIFEST_PUBLIC_PATH,
  ...CUSTOMER_PORTAL_PUBLIC_PATHS.apis,
  ...ICPOCHIPOCHI_PUBLIC_PATHS.apis,
  ...G_PLAN_PUBLIC_PATHS.apis,
  ...OPEN_PUBLIC_PATHS.apis,
  ...INTAKE_PUBLIC_PATHS.apis,
  ...HEARING_PUBLIC_PATHS.apis,
  ...BUILDING_APPLICATION_PUBLIC_PATHS.apis,
  ...CUSTOMER_MYPAGE_PUBLIC_PATHS.apis,
  ...EVENTS_PUBLIC_PATHS.apis,
  ...LINEWORKS_PUBLIC_PATHS.apis,
  ...INVITATIONS_PUBLIC_PATHS.apis,
  ...REFERRALS_PUBLIC_PATHS.apis,
  ...LIFE_X_PUBLIC_PATHS.apis,
  ...EXTERNAL_LIFEX_PUBLIC_PATHS.apis,
]
